import { Ident } from '@mode-switch/express';
import { Attr, Field, Sort, Values } from '@mode-switch/model';
import { Query } from './Query';
import { DomainQuery } from './DomainQuery';
import { PivotQuery } from './PivotQuery';
export var DomainPlan;
(function (DomainPlan) {
    function make(model, viewName, query) {
        switch (query.type) {
            case "TABLE":
                return table(model, viewName, query);
            case "PIVOT":
                return pivot(model, viewName, query);
        }
    }
    DomainPlan.make = make;
    function table(model, viewName, query) {
        const includesUnsupportedRoles = query.fields.some((field) => field.role !== "DIMENSION");
        if (includesUnsupportedRoles) {
            throw new Error(`Table Domain Queries only support dimensions`);
        }
        return pivot(model, viewName, DomainQuery.Pivot.make(PivotQuery.Basic.make({}), {
            fields: query.fields,
        }));
    }
    DomainPlan.table = table;
    function pivot(model, viewName, domainQuery) {
        const dQuery = domainQuery.query;
        const queries = plan(dQuery);
        if (queries.length === 0) {
            throw new Error(`Cannot plan Domain Pivot Query from an empty query`);
        }
        return domainQuery.fields.reduce((domain, field) => {
            switch (field.role) {
                case "DIMENSION": {
                    switch (field.type) {
                        case "CONTINUOUS": {
                            return continuousDimension(domain, field, queries[0]);
                        }
                        case "DISCRETE": {
                            return discreteDimension(domain, queries[0], field);
                        }
                    }
                }
                case "MEASURE": {
                    switch (field.type) {
                        case "CONTINUOUS": {
                            return continuousMeasure(domain, queries[0], dQuery.type, field);
                        }
                        case "DISCRETE": {
                            return discreteMeasure(domain, queries, field);
                        }
                    }
                }
            }
        }, {
            type: "DOMAIN",
            id: Ident.next(),
            model,
            viewName,
            queries: [],
            parts: [],
        });
    }
    DomainPlan.pivot = pivot;
    function addPart(domain, part, query) {
        const name = `domain:${part.field.alias}:part_${domain.parts.length}`;
        return {
            ...domain,
            parts: [...domain.parts, { ...part, query: name }],
            queries: [...domain.queries, { ...query, name }],
        };
    }
    DomainPlan.addPart = addPart;
    function extend(domain, source) {
        for (const part of source.parts) {
            const query = source.queries.find((query) => query.name === part.query);
            if (query !== undefined) {
                domain = addPart(domain, part, query);
            }
        }
        return domain;
    }
    DomainPlan.extend = extend;
})(DomainPlan || (DomainPlan = {}));
function plan(dQuery) {
    var _a, _b;
    const markGroups = [];
    switch (dQuery.type) {
        case "BASIC": {
            markGroups.push(PivotQuery.Marks.fields((_a = dQuery.marks) !== null && _a !== void 0 ? _a : {}));
            break;
        }
        case "DETAIL": {
            const series = (_b = dQuery.series) !== null && _b !== void 0 ? _b : [];
            markGroups.push(...series.map((series) => {
                var _a;
                return [series.field].concat(PivotQuery.Marks.fields((_a = series.marks) !== null && _a !== void 0 ? _a : {}));
            }));
            break;
        }
    }
    return markGroups.map((marks) => {
        var _a, _b, _c, _d, _e;
        const column = [];
        const x = [];
        const row = [];
        const y = [];
        const names = [];
        const values = [];
        let hasNames = false;
        let hasValues = false;
        const checkNamesAndValues = (field) => {
            switch (field.source) {
                case "NAMES":
                    hasNames = true;
                    break;
                case "VALUES":
                    hasValues = true;
                    break;
            }
        };
        for (const field of (_a = dQuery.column) !== null && _a !== void 0 ? _a : []) {
            checkNamesAndValues(field);
            column.push(field);
        }
        for (const field of (_b = dQuery.x) !== null && _b !== void 0 ? _b : []) {
            checkNamesAndValues(field);
            x.push(field);
        }
        for (const field of (_c = dQuery.row) !== null && _c !== void 0 ? _c : []) {
            checkNamesAndValues(field);
            row.push(field);
        }
        for (const field of (_d = dQuery.y) !== null && _d !== void 0 ? _d : []) {
            checkNamesAndValues(field);
            y.push(field);
        }
        for (const field of marks) {
            checkNamesAndValues(field);
        }
        const canUseSeries = dQuery.type === "DETAIL" &&
            marks.length !== 0 &&
            marks[0].source !== "VALUES";
        if (hasValues) {
            const qValues = (_e = dQuery.values) !== null && _e !== void 0 ? _e : [];
            if (qValues.length !== 0) {
                names.push(...qValues);
                values.push(...qValues);
            }
            else if (canUseSeries) {
                names.push(marks[0]);
                values.push(marks[0]);
            }
        }
        else if (hasNames) {
            if (canUseSeries) {
                names.push(marks[0]);
            }
        }
        const { filters, detail, sorts } = dQuery;
        return Query.Pivot.make({
            column,
            x,
            row,
            y,
            marks,
            names,
            values,
            filters,
            detail,
            sorts,
        });
    });
}
function isDiscreteField(f) {
    return f.type === "DISCRETE" && f.source !== "NAMES";
}
function dims(fields = []) {
    return fields.filter((field) => {
        return (field.role === "DIMENSION" && field.source !== "NAMES");
    });
}
function allMeas(pivot) {
    return [pivot.column, pivot.x, pivot.row, pivot.y, pivot.marks].flatMap((channel = []) => {
        return channel.filter((f) => {
            return f.role === "MEASURE" && f.source !== "VALUES";
        });
    });
}
function makeSorts(pivot, fields) {
    var _a;
    const sorts = (_a = pivot.sorts) !== null && _a !== void 0 ? _a : [];
    return fields.map((field) => {
        var _a;
        return (_a = sorts.find((s) => s.field.alias === field.alias)) !== null && _a !== void 0 ? _a : Sort.ByData.make(field);
    });
}
function nestedPath(pivot, field) {
    var _a, _b, _c, _d;
    const column = ((_a = pivot.column) !== null && _a !== void 0 ? _a : []).concat((_b = pivot.x) !== null && _b !== void 0 ? _b : []);
    const row = ((_c = pivot.row) !== null && _c !== void 0 ? _c : []).concat((_d = pivot.y) !== null && _d !== void 0 ? _d : []);
    for (const { axis, fields } of [
        { axis: 'x', fields: column },
        { axis: 'y', fields: row },
    ]) {
        const path = collect(fields);
        if (path !== undefined) {
            return {
                axis,
                path,
            };
        }
    }
    return {
        axis: undefined,
        path: [],
    };
    function collect(fields) {
        const fIndex = fields.findIndex((f) => f.alias === field.alias);
        if (fIndex !== -1) {
            return fields.slice(0, fIndex + 1).filter(isDiscreteField);
        }
    }
}
function discreteDimension(domain, pivot, field) {
    let column;
    let x;
    let row;
    let y;
    let marks = [field];
    const names = pivot.names;
    const values = pivot.values;
    let sorts = makeSorts(pivot, [field]);
    if (sorts.length !== 1) {
        throw new Error(`Expected sorts to have a length of 1, saw ${sorts.length}`);
    }
    const sort = sorts[0];
    if (sort.type === "BY_NESTED") {
        marks = [];
        const nested = nestedPath(pivot, field);
        switch (nested.axis) {
            case 'x': {
                sorts = makeSorts(pivot, nested.path);
                column = nested.path.slice(0, -1);
                x = nested.path.slice(-1);
                break;
            }
            case 'y': {
                sorts = makeSorts(pivot, nested.path);
                row = nested.path.slice(0, -1);
                y = nested.path.slice(-1);
                break;
            }
            default: {
                throw new Error('Nested sort field not found. Nested sorts require that the sort field ' +
                    'can be found on either {column,x} or {row,y} of the domain query');
            }
        }
    }
    if (field.source === "NAMES") {
        if ((values !== null && values !== void 0 ? values : []).length !== 0) {
            marks.push(Values.Field.make(Ident.next()));
        }
        else if ((names !== null && names !== void 0 ? names : []).length === 0) {
            const alias = Ident.next();
            marks.push(Field.categorical(Attr.dimension(`STR(NULL)`, field.formula.type), alias, alias));
        }
    }
    marks.push(...allMeas(pivot));
    const { filters } = pivot;
    const query = Query.Detail.extend(Query.Pivot.make({
        column,
        x,
        row,
        y,
        marks,
        names,
        values,
        filters,
        sorts,
    }), pivot);
    return DomainPlan.addPart(domain, {
        type: "DISCRETE",
        role: "DIMENSION",
        query: query.name,
        values: field,
        field,
    }, query);
}
function continuousDimension(domain, field, pivot) {
    const min = Field.ordinal(Attr.dimension(`MIN(${field.formula.source})`, field.formula.type), `MIN(${field.name})`, `min_${field.alias}`, "CONTINUOUS", "MEASURE");
    const max = Field.ordinal(Attr.dimension(`MAX(${field.formula.source})`, field.formula.type), `MAX(${field.name})`, `max_${field.alias}`, "CONTINUOUS", "MEASURE");
    const { filters } = pivot;
    const query = Query.Detail.extend(Query.Pivot.make({
        marks: [min, max],
        filters,
    }), pivot);
    return DomainPlan.addPart(domain, {
        type: "CONTINUOUS",
        role: "DIMENSION",
        query: query.name,
        field,
        min,
        max,
    }, query);
}
function discreteMeasure(domain, pivots, field) {
    return pivots.reduce((domain, pivot) => {
        const column = dims(pivot.column);
        const x = dims(pivot.x);
        const row = dims(pivot.row);
        const y = dims(pivot.y);
        const marks = dims(pivot.marks).concat(field);
        const sorts = makeSorts(pivot, [field]);
        const query = Query.Detail.extend(Query.Pivot.make({
            ...pivot,
            column,
            x,
            row,
            y,
            marks,
            sorts,
        }), pivot);
        return DomainPlan.addPart(domain, {
            type: "DISCRETE",
            role: "MEASURE",
            query: query.name,
            values: field,
            field,
        }, query);
    }, domain);
}
function continuousMeasure(domain, pivot, pType, field) {
    const min = field;
    const max = field;
    const column = dims(pivot.column);
    const x = dims(pivot.x);
    const row = dims(pivot.row);
    const y = dims(pivot.y);
    const marks = [field];
    if (pType === "BASIC") {
        marks.push(...dims(pivot.marks));
    }
    const { filters } = pivot;
    const query = Query.Detail.extend(Query.Pivot.make({
        column,
        x,
        row,
        y,
        marks,
        filters,
    }), pivot);
    return DomainPlan.addPart(domain, {
        type: "CONTINUOUS",
        role: "MEASURE",
        query: query.name,
        field,
        min,
        max,
    }, query);
}
